import { Box, Card, CardContent, Skeleton, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useDelayed } from './useDelayed';

interface ListSkeletonProps {
  /** Number of placeholder rows to render. Defaults to 5. */
  count?: number;
  /** Whether to render two chip placeholders below the title row. */
  withChips?: boolean;
  /** Translated accessible name of the busy container. Defaults to "Laadin…". */
  label?: string;
  /**
   * Render nothing for this long (UI-alus §1: nothing under 200 ms). Default 0
   * keeps the existing admin lists unchanged; new client/worker views use
   * `PageSkeleton`, which defaults to 200.
   */
  delayMs?: number;
}

/** The card-row shapes alone — shared with `PageSkeleton` so the two never drift. */
export function ListSkeletonRows({
  count = 5,
  withChips = true,
}: Pick<ListSkeletonProps, 'count' | 'withChips'>) {
  return (
    <Stack spacing={1} aria-hidden>
      {Array.from({ length: count }).map((_, i) => (
        <Card key={i} variant="outlined">
          <CardContent sx={{ py: 1.5 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Skeleton variant="text" width="55%" height={22} />
              <Skeleton variant="rounded" width={72} height={22} />
            </Stack>
            <Skeleton variant="text" width="35%" height={16} sx={{ mt: 0.25 }} />
            {withChips && (
              <Stack direction="row" spacing={0.5} sx={{ mt: 0.75 }}>
                <Skeleton variant="rounded" width={60} height={20} />
                <Skeleton variant="rounded" width={80} height={20} />
                <Skeleton variant="rounded" width={50} height={20} />
              </Stack>
            )}
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
}

/**
 * Card-list skeleton used while data is loading. Mirrors the shape
 * of our mobile card lists (title row + caption + chip row) so the
 * layout doesn't jump when data arrives. The container is a named,
 * `aria-busy` group; the shapes themselves are hidden from assistive tech.
 */
export function ListSkeleton({ count = 5, withChips = true, label, delayMs = 0 }: ListSkeletonProps) {
  const { t } = useTranslation();
  const visible = useDelayed(delayMs);
  if (!visible) return null;
  const name = label ?? t('common:ui.skeleton.loading', { defaultValue: 'Laadin…' });
  return (
    <Box role="group" aria-busy="true" aria-label={name}>
      <ListSkeletonRows count={count} withChips={withChips} />
    </Box>
  );
}
